<template>
  <a-modal
    :open="visible"
    title="반품 접수"
    @cancel="close"
    :width="isMobile ? '100%' : 520"
    :style="isMobile ? { top: 0, margin: 0, padding: 0, maxWidth: '100vw' } : {}"
    :body-style="isMobile ? { overflowY: 'auto', maxHeight: '90vh', padding: '20px 16px' } : { padding: '24px' }"
  >
    <!-- 주문 요약 (주문 상세 모달과 동일한 descriptions 레이아웃) -->
    <a-descriptions bordered :column="1" size="small" class="return-order-info">
      <a-descriptions-item label="주문번호">{{ order?.ord_no }}</a-descriptions-item>
      <a-descriptions-item label="상품명">{{ order?.prd_name }}</a-descriptions-item>
      <a-descriptions-item label="수량/단가">{{ order?.item_cnt }}개 x {{ formatPrice(order?.item_price) }}원</a-descriptions-item>
      <a-descriptions-item label="입점사">{{ partnerLabel(order?.partner_code) }}</a-descriptions-item>
      <a-descriptions-item label="주문금액">{{ formatPrice(order?.ord_price) }}원</a-descriptions-item>
    </a-descriptions>

    <!-- 반품 폼 -->
    <a-form layout="vertical" class="return-form">
      <a-form-item label="반품 사유">
        <a-select v-model:value="form.return_reason_code" placeholder="선택 (선택사항)" allow-clear>
          <a-select-option value="defective">불량/하자</a-select-option>
          <a-select-option value="wrong_item">오배송</a-select-option>
          <a-select-option value="changed_mind">단순변심</a-select-option>
          <a-select-option value="damaged_shipping">배송 중 파손</a-select-option>
          <a-select-option value="other">기타</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="상세 사유" v-if="form.return_reason_code">
        <a-input v-model:value="form.return_reason" placeholder="상세 사유 입력 (선택사항)" allow-clear />
      </a-form-item>
      <a-form-item label="수거 방법">
        <a-radio-group v-model:value="form.pickup_method">
          <a-radio value="courier_pickup">택배수거</a-radio>
          <a-radio value="direct_return">직접회수</a-radio>
          <a-radio value="store_visit">매장방문</a-radio>
        </a-radio-group>
      </a-form-item>
      <a-form-item label="반품 운송장 번호" :required="isVendorProduct">
        <a-input
          v-model:value="form.tracking_number"
          placeholder="운송장 번호 입력"
          allow-clear
        />
        <div v-if="isVendorProduct" class="field-hint">입점사 상품은 운송장 번호가 필수입니다</div>
      </a-form-item>
      <a-form-item label="메모">
        <a-textarea v-model:value="form.memo" placeholder="메모 (선택사항)" :maxlength="1000" show-count :rows="3" />
      </a-form-item>
    </a-form>

    <template #footer>
      <div class="modal-footer">
        <a-button @click="close">취소</a-button>
        <a-button type="primary" :loading="submitting" @click="submit">반품 접수</a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import brightmallOrdersApi from '@/apis/brightmallOrders.js';

// 반품 오류 코드 메시지 맵
const ERROR_CODE_MAP = {
  RETURN_ALREADY_REQUESTED: '이미 반품 접수된 주문입니다.',
  RETURN_NOT_ALLOWED: '반품 접수가 불가능한 상태입니다.',
  ORDER_NOT_FOUND: '주문을 찾을 수 없습니다.',
};

export default {
  name: 'ReturnRequestModal',
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    order: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:visible', 'success'],
  data() {
    return {
      submitting: false,
      isMobile: window.innerWidth <= 767,
      form: {
        return_reason_code: undefined,
        return_reason: '',
        pickup_method: 'direct_return',
        tracking_number: '',
        memo: '',
      },
    };
  },
  computed: {
    isVendorProduct() {
      return this.order?.partner_code && this.order.partner_code !== 'DNTM';
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },
  watch: {
    // 모달이 열릴 때마다 폼 초기화
    visible(val) {
      if (val) {
        this.resetForm();
      }
    },
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth <= 767;
    },
    formatPrice(val) {
      if (val === null || val === undefined) return '0';
      return Number(val).toLocaleString('ko-KR');
    },
    partnerLabel(code) {
      const map = { DNTM: '덴티움(자사)', SEIL: '세일글로벌' };
      return map[code] || code || '-';
    },
    close() {
      this.$emit('update:visible', false);
    },
    resetForm() {
      this.form = {
        return_reason_code: undefined,
        return_reason: '',
        pickup_method: 'direct_return',
        tracking_number: '',
        memo: '',
      };
    },
    async submit() {
      if (!this.order?.ord_prd_no) {
        this.$message?.error('주문 정보가 올바르지 않습니다.');
        return;
      }
      if (this.isVendorProduct && !this.form.tracking_number.trim()) {
        this.$message?.warning('입점사 상품은 운송장 번호를 입력해주세요');
        return;
      }
      this.submitting = true;
      try {
        // 운송장 번호는 백엔드 규격(숫자 10~15자리)에 맞춰 숫자만 전송
        const normalizedTrackingNumber = this.form.tracking_number.replace(/\D/g, '');
        const requestItem = {
          ord_prd_no: this.order.ord_prd_no,
          return_reason_code: this.form.return_reason_code || undefined,
          return_reason: this.form.return_reason || undefined,
          pickup_method: this.form.pickup_method,
          return_tracking_number: normalizedTrackingNumber || undefined,
          memo: this.form.memo || undefined,
        };
        // 빈 값 제거
        Object.keys(requestItem).forEach((k) => {
          if (requestItem[k] === '' || requestItem[k] === undefined) delete requestItem[k];
        });
        await brightmallOrdersApi.requestReturn({
          return_requests: [requestItem],
        });
        this.$message?.success('반품이 접수되었습니다.');
        this.$emit('success');
      } catch (e) {
        const errorCode = e?.response?.data?.code || e?.response?.data?.error_code;
        const errorMsg = ERROR_CODE_MAP[errorCode] || '반품 접수에 실패했습니다.';
        this.$message?.error(errorMsg);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.return-order-info {
  margin-bottom: 24px;

  :deep(.ant-descriptions-item-label) {
    width: 90px;
    font-weight: 500;
    color: #8d939a;
    background: #fafbfc;
  }

  :deep(.ant-descriptions-item-content) {
    color: #2f3337;
    font-weight: 500;
  }
}

.return-form {
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;

  :deep(.ant-form-item) {
    margin-bottom: 20px;
  }

  :deep(.ant-form-item-label > label) {
    font-weight: 600;
    font-size: 13px;
    color: #4b5158;
  }

  :deep(.ant-select-selector),
  :deep(.ant-input) {
    min-height: 40px;
  }
}

.field-hint {
  font-size: 12px;
  color: #fa8c16;
  margin-top: 6px;
  line-height: 1.4;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

// 모바일 (~ 767px)
@media screen and (max-width: 767px) {
  :deep(.ant-modal) {
    top: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    max-width: 100vw !important;
    width: 100vw !important;

    .ant-modal-content {
      border-radius: 0;
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      padding-top: env(safe-area-inset-top, 0px);
    }

    .ant-modal-header {
      position: sticky;
      top: 0;
      z-index: 10;
      min-height: 56px;
      display: flex;
      align-items: center;
      background: #fff;
      border-bottom: 1px solid #f0f0f0;

      .ant-modal-title {
        font-size: 17px;
        font-weight: 600;
      }
    }

    .ant-modal-close {
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;

      .ant-modal-close-x {
        width: 44px;
        height: 44px;
        line-height: 44px;
      }
    }

    .ant-modal-body {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      padding: 16px;
    }

    .ant-modal-footer {
      position: sticky;
      bottom: 0;
      background: #fff;
      border-top: 1px solid #f0f0f0;
      padding: 12px 16px;
      padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      display: flex;
      gap: 8px;

      .ant-btn {
        flex: 1;
        min-height: 48px;
        font-size: 15px;
      }
    }
  }

  // iOS 입력 줌 방지
  input, select, textarea {
    font-size: 16px !important;
  }
}
</style>
